import { COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS } from '@core/components/component-factory/component-factory-config-input.registry';
import type { ConfigComponentContract } from '@shared/utils/content-builder/config-component-contract';
import { computedResolvedConfig } from '@shared/utils/content-builder/block-config-computed-resolver';
import type { KioskCartResetTimerBlockConfig } from '@front-platform/content-builder';
import { kioskCartResetTimerBlockResolveConfig } from '@front-platform/content-builder';
import { Subject, timer } from 'rxjs';
import { NavigationEnd, Router } from '@angular/router';
import { debounceTime, takeUntil, tap } from 'rxjs/operators';
import { Component, HostListener, OnDestroy, OnInit, input } from '@angular/core';

import { DestroyerBase } from '@core/base/destroyer/destroyer.base';

import { ModalsService } from '@core/services/modals/modals.service';
import { ResetStateService } from '@core/services/reset-state.service';
import { AuthFirebaseService } from '@core/services/api/auth-firebase/auth-firebase.service';

import { WaitingModalComponent } from '@shared/modals/waiting-modal/waiting-modal.component';
import { KioskCartResetTimerModalComponent } from '@shared/modals/kiosk-cart-reset-timer-modal/kiosk-cart-reset-timer-modal.component';
export interface KioskCartResetTimerBlockRuntimeConfig {
  'wait_for_user_interaction_to_display_modal'?: boolean;
}

export type KioskCartResetTimerBlockConfigInput = Partial<
  Omit<KioskCartResetTimerBlockConfig, keyof KioskCartResetTimerBlockRuntimeConfig> &
    KioskCartResetTimerBlockRuntimeConfig
>;
export type KioskCartResetTimerBlockResolvedConfig = Omit<
  KioskCartResetTimerBlockConfig,
  keyof KioskCartResetTimerBlockRuntimeConfig
> &
  Partial<KioskCartResetTimerBlockRuntimeConfig>;
export interface KioskCartResetTimerBlockContract extends ConfigComponentContract<
  KioskCartResetTimerBlockConfigInput,
  KioskCartResetTimerBlockResolvedConfig
> {}

@Component({
  selector: 'app-kiosk-cart-reset-timer-block',
  template: ``,
  standalone: false,
})
export class KioskCartResetTimerBlockComponent
  extends DestroyerBase
  implements OnInit, OnDestroy, KioskCartResetTimerBlockContract
{
  readonly config = input<KioskCartResetTimerBlockConfigInput>({});
  readonly resolvedConfig = computedResolvedConfig<
    KioskCartResetTimerBlockConfig,
    KioskCartResetTimerBlockRuntimeConfig
  >(kioskCartResetTimerBlockResolveConfig, () => ({ config: this.config() }));
  get refreshAfterModalDelay(): number {
    return this.resolvedConfig()['refresh_after_modal_delay'] as unknown as number;
  }
  get refreshWithoutUserEventDelay(): number {
    return this.resolvedConfig()['refresh_without_user_event_delay'] as unknown as number;
  }
  get displayModalAfterUserEventDelay(): number {
    return this.resolvedConfig()['display_modal_after_user_event_delay'] as unknown as number;
  }
  get waitForUserInteractionToDisplayModal(): boolean {
    const value = this.resolvedConfig()['wait_for_user_interaction_to_display_modal'];
    return value === undefined ? true : (value as unknown as boolean);
  }

  protected timerEnabled = true;
  protected userEvents$ = new Subject<boolean>();

  constructor(
    protected readonly resetStateService: ResetStateService,
    protected readonly authFirebaseService: AuthFirebaseService,
    protected readonly modalsService: ModalsService,
    protected readonly router: Router,
  ) {
    super();
  }

  ngOnInit(): void {
    this.router.events
      .pipe(
        tap(event => {
          if (event instanceof NavigationEnd) {
            /* Prevent Timer on default url  */
            const segments = event.urlAfterRedirects.split('/').filter(segment => segment !== '');
            if (event.url.endsWith('payment') || segments.length === 1) {
              this.timerEnabled = false;
            } else {
              this.timerEnabled = true;
            }
          }
        }),
      )
      .subscribe();

    const displayDelay = Number(this.displayModalAfterUserEventDelay);
    if (Number.isFinite(displayDelay) && displayDelay > 0) {
      this.userEvents$
        .pipe(
          takeUntil(this._destroyerRef),
          debounceTime(displayDelay * 1000),
          takeUntil(this._destroyerRef),
          tap(() => this.displayModal()),
        )
        .subscribe();

      if (!this.waitForUserInteractionToDisplayModal) {
        this.userEvents$.next(true);
      }
    }

    const refreshDelay = Number(this.refreshWithoutUserEventDelay);
    if (Number.isFinite(refreshDelay) && refreshDelay > 0) {
      timer(refreshDelay * 1000)
        .pipe(
          takeUntil(this._destroyerRef),
          takeUntil(this.userEvents$),
          tap(() => this.refreshUI()),
        )
        .subscribe();
    }
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this.modalsService.close(KioskCartResetTimerModalComponent.handle);
  }

  @HostListener('document:mousedown', ['$event'])
  @HostListener('document:touchstart', ['$event'])
  @HostListener('window:scroll', ['$event'])
  clickOnDocument(_event: Event): void {
    this.userEvents$.next(true);
  }

  private displayModal(): void {
    if (!this.timerEnabled) {
      return;
    }
    const refreshDelay = Number(this.refreshAfterModalDelay);
    if (!Number.isFinite(refreshDelay) || refreshDelay <= 0) {
      return;
    }
    KioskCartResetTimerModalComponent.delay = this.refreshAfterModalDelay;
    this.modalsService.open(KioskCartResetTimerModalComponent.handle);
    timer(refreshDelay * 1000)
      .pipe(
        takeUntil(this._destroyerRef),
        takeUntil(this.userEvents$),
        tap(() => this.refreshUI()),
      )
      .subscribe();
  }

  private async refreshUI(): Promise<void> {
    if (!this.timerEnabled) {
      return;
    }
    this.modalsService.open(WaitingModalComponent.handle);
    timer(1).subscribe(() => {
      this.resetStateService.refreshUIAndResetLang();
      this.modalsService.close(KioskCartResetTimerModalComponent.handle);
    });
  }
}
COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS.set(KioskCartResetTimerBlockComponent, {
  'keyAliases': {},
});
